import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Message, StoredMessage } from './types';
import { UserDebugContext } from './UserDebugContext';

/** Max debug messages to retain (prevents memory leak from WebSocket traffic) */
const MAX_DEBUG_MESSAGES = 20;

/** Throttle interval for debug messages to prevent render storms (ms) */
const DEBUG_MESSAGE_THROTTLE_MS = 100;

export function UserDebugProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<StoredMessage[]>([]);

  // Throttle state updates to prevent render storms from high-frequency WebSocket messages
  const pendingMessages = useRef<StoredMessage[]>([]);
  const flushTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (flushTimeout.current) {
        clearTimeout(flushTimeout.current);
      }
    };
  }, []);

  const flushMessages = useCallback(() => {
    if (pendingMessages.current.length === 0) return;

    setMessages((s: StoredMessage[]) => {
      const newMessages = [...pendingMessages.current, ...s];
      pendingMessages.current = [];
      // Prevent unbounded growth - keep only recent messages
      return newMessages.length > MAX_DEBUG_MESSAGES
        ? newMessages.slice(0, MAX_DEBUG_MESSAGES)
        : newMessages;
    });
    flushTimeout.current = null;
  }, []);

  const logDebugMessage = useCallback(
    (msg: Message) => {
      pendingMessages.current.unshift({ ...msg, timestamp: Date.now() });

      // Throttle: batch updates and flush after interval
      if (!flushTimeout.current) {
        flushTimeout.current = setTimeout(
          flushMessages,
          DEBUG_MESSAGE_THROTTLE_MS
        );
      }
    },
    [flushMessages]
  );

  return (
    <UserDebugContext.Provider value={{ messages, logDebugMessage }}>
      {children}
    </UserDebugContext.Provider>
  );
}
